import MenuIcon from "@mui/icons-material/Menu";
import { PaletteMode } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import * as React from "react";
import ToggleColorMode from "./ToggleColorMode";

const logoStyle = {
  width: "115px",
  height: "auto",
  cursor: "pointer",
};

interface AppAppBarProps {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

interface NavigationItem {
  sectionId: string;
  label: string;
}

const navigationItems: NavigationItem[] = [
  { sectionId: "hero", label: "Visao geral" },
  { sectionId: "features", label: "Modulos" },
  { sectionId: "agenda-ritmo", label: "Agenda inteligente" },
  { sectionId: "crescimento", label: "Crescimento" },
];

function AppAppBar({ mode, toggleColorMode }: AppAppBarProps) {
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const sectionElement = document.getElementById(sectionId);
    const offset = 98;
    if (!sectionElement) {
      return;
    }

    const targetScroll = sectionElement.offsetTop - offset;
    window.scrollTo({
      top: targetScroll,
      behavior: "smooth",
    });
    setOpen(false);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        boxShadow: 0,
        bgcolor: "transparent",
        backgroundImage: "none",
        top: 0,
        py: 1,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          variant="regular"
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "999px",
            bgcolor:
              theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.68)"
                : "rgba(8, 16, 25, 0.82)",
            backdropFilter: "blur(14px)",
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              theme.palette.mode === "light"
                ? "0 8px 30px rgba(15, 74, 133, 0.12)"
                : "0 8px 30px rgba(2, 16, 35, 0.65)",
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <img
              src="/static/logo/logo.svg"
              style={logoStyle}
              alt="logo do Alttime"
            />
            <Chip label="Beta" color="primary" size="small" />
          </Box>

          <Box sx={{ display: { xs: "none", lg: "flex" }, gap: 0.4 }}>
            {navigationItems.map((item) => (
              <Button
                key={item.sectionId}
                onClick={() => scrollToSection(item.sectionId)}
                size="small"
              >
                {item.label}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 0.6,
              alignItems: "center",
            }}
          >
            <ToggleColorMode mode={mode} toggleColorMode={toggleColorMode} />
            <Button color="primary" variant="text" size="small" href="/login">
              Entrar
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="small"
              href="/register"
            >
              Criar conta
            </Button>
          </Box>

          <Box
            sx={{ display: { xs: "flex", md: "none" }, alignItems: "center" }}
          >
            <ToggleColorMode mode={mode} toggleColorMode={toggleColorMode} />
            <Button
              variant="text"
              color="primary"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{ minWidth: 36, p: "4px" }}
            >
              <MenuIcon />
            </Button>
          </Box>

          <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
            <Box
              sx={{
                minWidth: "72vw",
                p: 2.5,
                backgroundColor: "background.paper",
                height: "100%",
              }}
            >
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Navegacao
              </Typography>
              <Divider />
              <List>
                {navigationItems.map((item) => (
                  <ListItem
                    key={item.sectionId}
                    disablePadding
                    sx={{ mb: 0.6 }}
                  >
                    <Button
                      onClick={() => scrollToSection(item.sectionId)}
                      sx={{ justifyContent: "flex-start", width: "100%" }}
                    >
                      {item.label}
                    </Button>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ mt: 0.4, mb: 1.6 }} />
              <Button
                color="primary"
                variant="contained"
                href="/register"
                sx={{ width: "100%", mb: 1 }}
              >
                Criar conta
              </Button>
              <Button
                color="primary"
                variant="outlined"
                href="/login"
                sx={{ width: "100%" }}
              >
                Entrar
              </Button>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default AppAppBar;
